import { Link } from '@solidjs/router';
import { createEffect, createSignal, Show, useContext } from 'solid-js';
import { DOMElement } from 'solid-js/jsx-runtime';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Badge } from '../../ui-components/badge/badge';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { AtosLogoRelative } from '../icons-library/atos-logo-relative';
import { ImageSrcData } from '../image-component/image-component';
import IntersectionObserver from '../intersection-observer/intersection-observer';
import { showpadImageProxyUrl } from '../../tools/showpad-image-proxy-url';
import { CourseTag } from '../course/course-types';

export type CourseCardData = {
    courseId: string;
    name: string;
    description: string;
    progress: number;
    learnMinutes: number;
    completedDate: string;
    coverImageUrl: {
        small: string;
    };
    path?: {
        id: string;
        name: string;
    };
    curriculum?: {
        id: string;
        name: string;
    };
    durationInSeconds: number;
    tags?: CourseTag[]
};

type CourseCardProps = {
    course: CourseCardData;
    hideFlag?: boolean;
};

export const CourseCard = (props: CourseCardProps) => {
    const {
        localize,
        coursesEntityName,
    } = useContext(AppContext);

    const [ backgroundImageSrcData, setBackgroundImageSrcData ] = createSignal<ImageSrcData>();
    const [ lowResBackgroundImagePath, setLowResBackgroundImagePath ] = createSignal<string>();

    const isCompleted = () => !!props.course.completedDate;

    const inProgress = () =>
        !isCompleted() &&
            !!props.course.learnMinutes &&
            props.course.learnMinutes > 0;

    const showBadge = () => !props.hideFlag && (inProgress() || isCompleted());

    const setImage = () => {
        if (!props?.course.coverImageUrl.small) return;

        setLowResBackgroundImagePath(props.course.coverImageUrl.small);
    };

    createEffect(setImage);

    // We set the image both on createEffect for the frontend and call it directly so that the SSR also sets the image
    setImage();

    const showImage = (visible: boolean | DOMElement) => {
        if (!visible || !props?.course.coverImageUrl.small) {
            return;
        }

        const url = showpadImageProxyUrl(props.course.coverImageUrl.small);

        setBackgroundImageSrcData({
            original: url,
            retina: url,
            jwt: '',
        });
    };

    const StatusBadge = () => (
        <Show when={showBadge()}>
            <Badge
                variant={isCompleted() ? 'status-success' : 'highlight'}
                class="absolute right-cvi-6 top-cvi-6 z-[2]"
            >
                {isCompleted()
                    ? localize('completed', 'Completed')
                    : localize('in-progress', 'In progress')
                }
            </Badge>
        </Show>
    );

    const CourseImage = () => (
        <Show
            when={props.course.coverImageUrl.small}
            fallback={
                <div class="h-full bg-white rounded-t-cvi-md w-full object-cover object-center flex flex-col justify-center items-center text-[1.5rem] border-b border-gray-light">
                    <AtosLogoRelative />
                </div>
            }
        >
            <img
                src={lowResBackgroundImagePath()}
                alt=""
                role="presentation"
                class="bg-beige-light rounded-t-cvi-md w-full object-cover object-center h-full absolute top-0 left-0 z-0 [animation-delay:0.05s]"
                style={{
                    animation: backgroundImageSrcData()
                        ? 'image-fade-out 0.8s ease-out forwards'
                        : 'none',
                }}
            />
            <img
                src={backgroundImageSrcData()?.original || lowResBackgroundImagePath()}
                alt={localize('e-learning-course-cover-image', 'E-Learning Course Cover Image')}
                class="bg-beige-light rounded-t-cvi-md w-full object-cover object-center h-full absolute top-0 left-0 z-0 [animation-delay:0.05s]"
                style={{
                    animation: backgroundImageSrcData()
                        ? 'image-fade-out 0.8s ease-out backwards'
                        : 'none',
                }}
            />
        </Show>
    );

    const BottomText = () => (
        <div class="flex flex-col px-[2rem] py-[2rem]">
            <Show when={props.course.curriculum}>
                <p class="m-0 mb-1 body-xs font-cvi-regular text-gray line-clamp-1">
                    {props.course.curriculum!.name}
                </p>
            </Show>

            <Heading tag="h4" variant="tinyGray" lineLimit={2}>
                {props.course.name}
            </Heading>

            <Text fontSize='small' color='gray' lineLimit={3} noBlockSpacing>
                {props.course.description}
            </Text>
        </div>
    );

    return (
        <ErrorCatcher componentName='CourseCard'>
            <IntersectionObserver onVisible={showImage} threshold={0.1} className="h-full" style={{ 'z-index': 0 }}>
                <Link
                    href={`/${coursesEntityName}/${props.course.courseId}`}
                    class="flex flex-col rounded-cvi-md h-full w-full bg-white list-none relative no-underline border border-[#BEB9B9] transition-[transform,box-shadow] duration-200 hover:shadow-[0.2rem_0.2rem_0.86rem_0px_rgba(0,0,0,0.09)] hover:scale-[0.97]"
                >
                    <div class="relative h-[15rem] shrink-0">
                        <StatusBadge />
                        <CourseImage />
                    </div>

                    <div class="relative flex-1 border-t border-beige">
                        <BottomText />
                    </div>

                </Link>
            </IntersectionObserver>
        </ErrorCatcher>
    );
};
